'use client'

import { useState, useTransition } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { sendFeedback } from '@/app/feedback/actions'
import { actionErrorMessage } from '@/lib/action-error'
import { BUILD_ID } from '@/lib/app-version'
import { FEEDBACK_TEXT_MAX, normalizeFeedbackText, type FeedbackKind } from '@/lib/feedback'

// Форма "Пожелание" и "Нашёл ошибку" (.scratch/autopilot/feedback.md). Живёт
// внизу окна помощника (tutor-fab.tsx) и в настройках (feedback-panel.tsx).
// Ученик пишет только текст: экран, браузер и номер сборки прикладываем сами,
// адрес почты берётся из аккаунта. Inline-стили, как у соседних форм: телефон
// может держать устаревший CSS-бандл.

type Props = {
  initialKind?: FeedbackKind
  // В настройках сервер знает, вошёл ли ученик; в окне помощника не передаём,
  // гостю ответит server action.
  signedIn?: boolean
  // Кнопка "К вопросам" в окне помощника.
  onBack?: () => void
}

const PLACEHOLDER: Record<FeedbackKind, string> = {
  wish: 'Чего не хватает или что сделать удобнее?',
  bug: 'Что пошло не так и на каком шаге? Урок увидим сами.',
}

const muted: React.CSSProperties = { fontSize: 13, lineHeight: 1.45, color: 'var(--tk-fg-muted)' }

function deviceLine(): string {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return ''
  const ua = navigator.userAgent.replace(/\s+/g, ' ').trim().slice(0, 240)
  return `${ua} ${window.innerWidth}x${window.innerHeight}`
}

export function FeedbackForm({ initialKind = 'wish', signedIn = true, onBack }: Props) {
  const pathname = usePathname()
  const [kind, setKind] = useState<FeedbackKind>(initialKind)
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState<FeedbackKind | null>(null)
  const [pending, startTransition] = useTransition()

  if (!signedIn) {
    return (
      <div style={{ fontSize: 15, lineHeight: 1.5 }}>
        Пожелания и ошибки принимаем от вошедших учеников, так мы видим урок и можем ответить.{' '}
        <Link href={`/auth/login?next=${encodeURIComponent(pathname || '/settings')}`} style={{ color: 'var(--tk-ice)', textDecoration: 'underline' }}>
          Войти
        </Link>
        . Без входа можно написать на support@turkido.ru.
      </div>
    )
  }

  function submit() {
    const norm = normalizeFeedbackText(text)
    if ('error' in norm) {
      setError(norm.error)
      return
    }
    setError(null)
    startTransition(async () => {
      try {
        const res = await sendFeedback({ kind, text: norm.text, page: pathname, device: deviceLine(), build: BUILD_ID })
        if (res.ok) {
          setSent(kind)
          setText('')
        } else setError(res.error)
      } catch (e) {
        setError(actionErrorMessage(e, 'Не получилось отправить. Попробуйте ещё раз.'))
      }
    })
  }

  if (sent) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 16, lineHeight: 1.5 }}>
          Получили, спасибо. {sent === 'bug' ? 'Проверим и поправим.' : 'Подумаем, как это сделать.'} Если понадобится уточнить, напишем на почту
          аккаунта.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="tk-pill tk-pill--sm" onClick={() => setSent(null)}>
            Написать ещё
          </button>
          {onBack && (
            <button type="button" className="tk-pill tk-pill--sm" onClick={onBack}>
              К вопросам
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="flex items-center gap-2" role="tablist" aria-label="Что это">
        <button type="button" role="tab" aria-selected={kind === 'wish'} className="tk-pill-toggle" data-on={kind === 'wish' ? '' : undefined} onClick={() => setKind('wish')}>
          Пожелание
        </button>
        <button type="button" role="tab" aria-selected={kind === 'bug'} className="tk-pill-toggle" data-on={kind === 'bug' ? '' : undefined} onClick={() => setKind('bug')}>
          Нашёл ошибку
        </button>
      </div>
      {/* Награда за принятые предложения (тикет 96). */}
      <div className="text-[13px] text-fg-muted">За принятые предложения даём 100 akçe, за крупные идеи месяц Plus.</div>
      <textarea
        className="tk-text-answer"
        rows={5}
        maxLength={FEEDBACK_TEXT_MAX}
        placeholder={PLACEHOLDER[kind]}
        value={text}
        disabled={pending}
        onChange={(e) => setText(e.target.value)}
        style={{ resize: 'vertical', minHeight: 120, lineHeight: 1.45 }}
        aria-label={kind === 'bug' ? 'Что пошло не так' : 'Пожелание'}
      />
      <div style={muted}>Экран и браузер прикрепим сами. Ответ, если нужен, придёт на почту аккаунта.</div>
      {error && (
        <div className="text-sm" style={{ color: 'oklch(0.75 0.1 25)' }}>
          {error}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="tk-btn-primary"
          disabled={pending || !text.trim()}
          onClick={submit}
          style={{ height: 44, padding: '0 22px', borderRadius: 9999, fontSize: 16, fontWeight: 700 }}
        >
          {pending ? 'Отправляем...' : 'Отправить'}
        </button>
        {onBack && (
          <button type="button" className="tk-pill tk-pill--sm" onClick={onBack}>
            К вопросам
          </button>
        )}
      </div>
    </div>
  )
}
